import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "../supabase";

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const SPIN_LOGO = "https://i.imgur.com/v44wVQV.gif";
const LOADING_MS = 2000;
const SWITCH_MS = 6000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ─── helpers ─── */
function fmt(price, currency) {
  if (!price) return null;
  return new Intl.NumberFormat("en-IL", {
    style: "currency", currency: currency || "ILS", maximumFractionDigits: 0,
  }).format(price);
}

function getCountdown(lockedUntil) {
  const target = new Date(lockedUntil + "T00:00:00+02:00");
  const diff = target - new Date();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

/* ─── Digital download modal ─── */
function DigitalDownloadModal({ item, onConfirm, onCancel }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0a0a0a", border: "1px solid rgba(240,237,232,0.1)", maxWidth: "380px", width: "100%", padding: "44px 36px", textAlign: "center" }}>
        <p style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.45em", textTransform: "uppercase", color: "rgba(240,237,232,0.3)", marginBottom: "20px" }}>Digital Download</p>
        <p style={{ fontFamily: F, fontSize: "16px", fontWeight: 900, color: "#f0ede8", marginBottom: "24px", lineHeight: 1.2 }}>{item.name}</p>
        <p style={{ fontFamily: F, fontSize: "11px", color: "rgba(240,237,232,0.45)", lineHeight: 1.7, marginBottom: "32px" }}>You will be forwarded to Bandcamp to complete your purchase. A download link arrives by email.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={onCancel} style={{ fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", padding: "10px 20px", border: "1px solid rgba(240,237,232,0.18)", background: "transparent", color: "rgba(240,237,232,0.4)", cursor: "pointer" }}>Cancel</button>
          <button onClick={onConfirm} style={{ fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", padding: "10px 24px", border: "1px solid #f0ede8", background: "#f0ede8", color: "#000", cursor: "pointer" }}>Continue</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Countdown ─── */
function CountdownBadge({ lockedUntil, compact }) {
  const [cd, setCd] = useState(() => getCountdown(lockedUntil));
  useEffect(() => {
    const id = setInterval(() => setCd(getCountdown(lockedUntil)), 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);
  if (!cd) return null;
  if (compact) return (
    <span style={{ fontFamily: "monospace", fontSize: "9px", color: "rgba(240,237,232,0.35)", letterSpacing: "0.04em" }}>
      {cd.d}d {String(cd.h).padStart(2, "0")}h {String(cd.m).padStart(2, "0")}m {String(cd.s).padStart(2, "0")}s
    </span>
  );
  return (
    <div style={{ display: "flex", gap: "14px", justifyContent: "center" }}>
      {[["d", cd.d], ["h", cd.h], ["m", cd.m], ["s", cd.s]].map(([unit, val]) => (
        <div key={unit} style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", fontSize: "22px", fontWeight: 700, color: "rgba(240,237,232,0.75)", lineHeight: 1 }}>{String(val).padStart(2, "0")}</div>
          <div style={{ fontFamily: F, fontSize: "7px", letterSpacing: "0.2em", color: "rgba(240,237,232,0.2)", marginTop: "5px", textTransform: "uppercase" }}>{unit}</div>
        </div>
      ))}
    </div>
  );
}

function LockIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="rgba(240,237,232,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/* ─── Rain layer spanning the full stage area ─── */
function RainLayer() {
  const drops = useMemo(() => Array.from({ length: 44 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    height: 40 + Math.random() * 90,
    dur: 0.7 + Math.random() * 1.1,
    delay: Math.random() * 2,
    opacity: 0.3 + Math.random() * 0.5,
  })), []);
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none", overflow: "hidden" }}>
      {drops.map(d => (
        <div key={d.id} style={{
          position: "absolute", top: "-20%", left: `${d.left}%`, width: "1px", height: `${d.height}px`,
          background: "linear-gradient(to bottom, transparent, rgba(240,237,232,0.28), rgba(240,237,232,0.05))",
          opacity: d.opacity,
          animation: `ashdod-fall ${d.dur}s linear infinite`, animationDelay: `-${d.delay}s`,
        }} />
      ))}
    </div>
  );
}

/* ─── Single product stage — full-bleed, crisp edges, slow luxury fade on switch ─── */
function ProductStage({ item }) {
  const [showModal, setShowModal] = useState(false);

  const isLocked = item.locked_until && getCountdown(item.locked_until) !== null;
  const isSoldOut = item.sold_out;
  const isDisabled = isLocked || isSoldOut;
  const isDigital = item.badge === "Digital Download";

  const tags = useMemo(() => {
    if (!item.tags) return [];
    if (Array.isArray(item.tags)) return item.tags;
    try { return JSON.parse(item.tags); } catch { return []; }
  }, [item.tags]);

  function handleShopClick(e) {
    if (isDigital) { e.preventDefault(); setShowModal(true); }
  }
  function confirmRedirect() {
    setShowModal(false);
    window.open(item.shopify_url, "_blank", "noopener,noreferrer");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", animation: "ashdod-luxury-fade 1.6s ease", padding: "18px 0" }}>
      {showModal && <DigitalDownloadModal item={item} onConfirm={confirmRedirect} onCancel={() => setShowModal(false)} />}

      <div style={{ position: "relative", width: "94%", maxWidth: "420px", aspectRatio: "3/4", overflow: "hidden", background: "#000" }}>
        {item.image_url && (
          <img src={item.image_url} alt={item.name} loading="lazy" style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block",
            filter: isLocked ? "brightness(0.18) grayscale(1)" : isSoldOut ? "brightness(0.35) grayscale(0.8)" : "brightness(0.92)",
          }} />
        )}

        {isSoldOut && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: "#f0ede8", border: "1px solid rgba(240,237,232,0.4)", padding: "5px 12px", background: "rgba(0,0,0,0.65)" }}>SOLD OUT</span>
          </div>
        )}
        {isLocked && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px", padding: "16px" }}>
            <LockIcon size={22} />
            <CountdownBadge lockedUntil={item.locked_until} compact={false} />
          </div>
        )}
        {isDigital && !isLocked && !isSoldOut && (
          <div style={{ position: "absolute", top: "10px", left: "10px", fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.28em", textTransform: "uppercase", padding: "3px 8px", border: "1px solid rgba(240,237,232,0.45)", color: "#f0ede8", background: "rgba(0,0,0,0.75)" }}>DIGITAL</div>
        )}
        {item.badge === "Coming Soon" && !isLocked && !isSoldOut && (
          <div style={{ position: "absolute", top: "10px", left: "10px", fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: "#f0ede8", border: "1px solid rgba(240,237,232,0.4)", padding: "3px 10px", background: "rgba(0,0,0,0.65)" }}>COMING SOON</div>
        )}

      </div>

      <div style={{ textAlign: "center", marginTop: "22px" }}>
        {item.drop_label && (
          <p style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.35em", textTransform: "uppercase", color: "rgba(240,237,232,0.2)", marginBottom: "6px" }}>{item.drop_label}</p>
        )}
        <h3 style={{ fontFamily: F, fontSize: "13px", fontWeight: 700, color: "#f0ede8", marginBottom: "8px", lineHeight: 1.3, letterSpacing: "-0.01em" }}>{item.name}</h3>
        {tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", justifyContent: "center", marginBottom: "10px" }}>
            {tags.map(t => <span key={t} style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", padding: "3px 7px", border: "1px solid rgba(240,237,232,0.15)", color: "rgba(240,237,232,0.4)" }}>{t}</span>)}
          </div>
        )}
        {isLocked ? <CountdownBadge lockedUntil={item.locked_until} compact={true} />
          : isSoldOut ? <span style={{ fontFamily: F, fontSize: "9px", color: "rgba(240,237,232,0.25)", letterSpacing: "0.15em", textTransform: "uppercase", display: "block" }}>Sold out</span>
          : item.price ? <p style={{ fontFamily: F, fontSize: "11px", fontWeight: 300, color: "rgba(240,237,232,0.35)", letterSpacing: "0.02em", marginBottom: "14px" }}>{fmt(item.price, item.currency)}</p>
          : null}

        {!isDisabled && item.shopify_url && (
          <a href={item.shopify_url} target="_blank" rel="noopener noreferrer" onClick={handleShopClick}
            style={{
              display: "inline-block", marginTop: "6px", fontFamily: F, fontSize: "9px", fontWeight: 700,
              letterSpacing: "0.35em", textTransform: "uppercase", color: "#f0ede8",
              border: "1px solid rgba(240,237,232,0.4)", padding: "13px 44px",
              textDecoration: "none", cursor: "pointer",
            }}
          >
            BUY
          </a>
        )}
      </div>
    </div>
  );
}

/* ─── Email capture — posts to api/subscribe-email.js, which appends to a Google Sheet ─── */
function EmailCapture() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function handleChange(e) {
    setEmail(e.target.value);
    if (error) { setError(false); setErrorMsg(""); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError(true);
      setErrorMsg("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/subscribe-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "ashdod" }),
      });
      if (!res.ok) throw new Error("request failed");
      setSubmitted(true);
    } catch {
      setError(true);
      setErrorMsg("Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: "400px", margin: "0 auto", padding: "32px 24px 56px", textAlign: "center" }}>
      <p style={{ fontFamily: F, fontSize: "15px", fontWeight: 700, letterSpacing: "0.04em", color: "#f0ede8", marginBottom: "22px", lineHeight: 1.4 }}>
        JOIN FOR EARLY ACCESS TO THE NEXT DROP.
      </p>

      {submitted ? (
        <p style={{ fontFamily: F, fontSize: "12px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#f0ede8" }}>
          You're on the list.
        </p>
      ) : (
        <>
          <form onSubmit={handleSubmit} style={{ display: "flex", alignItems: "stretch", border: error ? "2px solid rgba(220,120,120,0.7)" : "2px solid #f0ede8" }}>
            <input
              type="email"
              value={email}
              onChange={handleChange}
              placeholder="YOUR EMAIL"
              disabled={submitting}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                color: "#f0ede8", fontFamily: F, fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em",
                padding: "17px 18px",
              }}
            />
            <button type="submit" aria-label="Join" disabled={submitting} style={{
              border: "none", background: submitting ? "rgba(240,237,232,0.4)" : "#f0ede8",
              color: "#000", fontFamily: F, fontSize: "10px", fontWeight: 700, letterSpacing: "0.25em",
              textTransform: "uppercase", padding: "0 24px",
              cursor: submitting ? "default" : "pointer",
            }}>{submitting ? "…" : "JOIN"}</button>
          </form>
          {error && (
            <p style={{ fontFamily: F, fontSize: "9px", color: "rgba(220,120,120,0.8)", marginTop: "10px", letterSpacing: "0.05em" }}>
              {errorMsg}
            </p>
          )}
        </>
      )}

      <p style={{ fontFamily: F, fontSize: "8px", color: "rgba(240,237,232,0.28)", marginTop: "16px", lineHeight: 1.6 }}>
        By joining, you agree to receive email from YEN SOUND.
      </p>
    </div>
  );
}

/* ─── Shop-all panel: inline browse strip so people never have to leave the page ─── */
function ShopAllPanel({ items, activeIndex, onSelect, onOpenChange }) {
  const [open, setOpen] = useState(false);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (onOpenChange) onOpenChange(next);
  }

  return (
    <div style={{ position: "relative", textAlign: "center", padding: "36px 20px 8px", overflow: "hidden" }}>
      <img src={SPIN_LOGO} alt="" aria-hidden="true" style={{
        position: "absolute", top: "50%", left: "50%",
        width: "260px", height: "260px", objectFit: "contain",
        transform: "translate(-50%, -50%)",
        opacity: 0.07, filter: "grayscale(1) brightness(1.6)",
        pointerEvents: "none",
      }} />
      <button onClick={toggle} style={{
        position: "relative", zIndex: 1,
        background: "transparent", border: "none", cursor: "pointer", padding: 0,
        display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "12px",
      }}>
        <span style={{ fontFamily: F, fontSize: "10px", fontWeight: 700, letterSpacing: "0.14em", color: "rgba(240,237,232,0.55)", display: "flex", gap: "8px", alignItems: "center" }}>
          <span dir="ltr" style={{ letterSpacing: "0.2em" }}>YEN SOUND</span>
          <span style={{ color: "rgba(240,237,232,0.28)" }}>×</span>
          <span dir="rtl">מיוצר באשדוד</span>
        </span>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase",
          color: "rgba(240,237,232,0.55)", paddingBottom: "3px", borderBottom: "1px solid rgba(240,237,232,0.25)",
        }}>
          {open ? "CLOSE" : "VIEW ALL"}
          <span style={{
            display: "inline-block", width: "5px", height: "5px",
            borderRight: "1px solid rgba(240,237,232,0.55)", borderBottom: "1px solid rgba(240,237,232,0.55)",
            transform: open ? "rotate(-135deg)" : "rotate(45deg)",
            transition: "transform 0.25s ease",
          }} />
        </span>
      </button>

      {open && (
        <div style={{
          display: "flex", gap: "10px", overflowX: "auto", padding: "22px 4px 6px",
          justifyContent: items.length <= 5 ? "center" : "flex-start",
        }}>
          {items.map((it, i) => (
            <button
              key={it.id}
              onClick={() => onSelect(i)}
              aria-label={it.name}
              style={{
                flex: "0 0 auto", width: "56px", height: "72px", position: "relative", padding: 0,
                background: "#000", cursor: "pointer", overflow: "hidden",
                border: i === activeIndex ? "1px solid rgba(240,237,232,0.8)" : "1px solid rgba(240,237,232,0.15)",
              }}
            >
              {it.image_url && (
                <img src={it.image_url} alt={it.name} style={{
                  width: "100%", height: "100%", objectFit: "cover",
                  opacity: i === activeIndex ? 1 : 0.55,
                }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Loading screen: enlarged, branded YEN SOUND × מיוצר באשדוד splash ─── */
function LoadingScreen({ visible }) {
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 40, background: "#000",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "28px",
      opacity: visible ? 1 : 0, pointerEvents: visible ? "auto" : "none",
      transition: "opacity 0.5s ease",
    }}>
      <div style={{ position: "relative", width: "220px", height: "220px" }}>
        <img src={SPIN_LOGO} alt="Made in Ashdod" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
      </div>
      <p style={{ fontFamily: F, fontSize: "18px", fontWeight: 700, letterSpacing: "0.08em", color: "#f0ede8", display: "flex", gap: "14px", alignItems: "center" }}>
        <span dir="ltr" style={{ letterSpacing: "0.22em" }}>YEN SOUND</span>
        <span style={{ color: "rgba(240,237,232,0.35)", fontSize: "16px" }}>×</span>
        <span dir="rtl">מיוצר באשדוד</span>
      </p>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function MadeInAshdod() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const [stageIndex, setStageIndex] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from("merch")
        .select("*")
        .eq("active", true)
        .order("sort_order", { ascending: true });
      const showerItems = (data || []).filter(i =>
        (i.artist || "").toLowerCase().includes("shower") ||
        (i.artist_slug || "").toLowerCase().includes("shower") ||
        (i.name || "").toLowerCase().includes("ashdod") ||
        (i.drop_label || "").toLowerCase().includes("ashdod")
      );
      setItems(showerItems);
      setLoading(false);
    }
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setShowLoadingScreen(false), LOADING_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (items.length <= 1 || isPanelOpen) return;
    const interval = setInterval(() => {
      setStageIndex(i => (i + 1) % items.length);
    }, SWITCH_MS);
    return () => clearInterval(interval);
  }, [items.length, isPanelOpen]);

  const currentItem = items[stageIndex];

  return (
    <>
      <Helmet>
        <title>Made in Ashdod — SHOWER × YEN SOUND</title>
        <meta name="description" content="SHOWER limited capsule collection. Made in Ashdod." />
      </Helmet>

      <style>{`
        @keyframes ashdod-fall { from { transform: translateY(0); } to { transform: translateY(140vh); } }
        @keyframes ashdod-luxury-fade {
          0%   { opacity: 0; }
          40%  { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>

      <div style={{ backgroundColor: "#000", minHeight: "100vh", position: "relative", paddingTop: "60px" }}>
        <LoadingScreen visible={showLoadingScreen} />

        {!loading && items.length > 0 && (
          <ShopAllPanel
            items={items}
            activeIndex={stageIndex}
            onSelect={setStageIndex}
            onOpenChange={setIsPanelOpen}
          />
        )}

        <div style={{ position: "relative", padding: "28px 0 0" }}>
          <RainLayer />

          {loading ? (
            <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.18, padding: "80px 0", textAlign: "center" }}>Loading...</p>
          ) : items.length === 0 ? (
            <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.18, padding: "80px 0", textAlign: "center" }}>Collection coming soon.</p>
          ) : (
            <div style={{ position: "relative", zIndex: 1 }}>
              <ProductStage key={currentItem.id} item={currentItem} />
            </div>
          )}
        </div>

        <EmailCapture />
      </div>
    </>
  );
}
